/**
 * AST-based Undo/Redo Manager
 * Manages undo/redo operations for AST content with coalescing and selection tracking
 */

import { v4 as uuidv4 } from 'uuid'

import {
  AstOperation,
  AstTransaction,
  CoalescingConfig,
  canCoalesceOperations,
  coalesceOperations,
  createInverseOperation,
  defaultCoalescingConfig,
} from './astOperations'
import { ApplyResult, applyOperations } from './operationApplier'
import { logger } from '../../utils/logger'

import { AstSelection, ContentAST, cloneAST, findAnyById, normalizeAST } from './index'

export interface UndoRedoManagerOptions {
  maxHistorySize?: number
  coalescingConfig?: Partial<CoalescingConfig>
  enableNormalization?: boolean
}

export interface UndoRedoState {
  canUndo: boolean
  canRedo: boolean
  undoDescription?: string
  redoDescription?: string
  historySize: number
}

/**
 * AST-based Undo/Redo Manager
 */
export class AstUndoRedoManager {
  private ast: ContentAST
  private selection: AstSelection
  private undoStack: AstTransaction[] = []
  private redoStack: AstTransaction[] = []
  private options: Required<UndoRedoManagerOptions>
  private coalescingConfig: CoalescingConfig
  private isApplyingTransaction = false
  private isUpdatingSelection = false
  private pendingEditorState: unknown | null = null
  private currentEditorState: unknown | null = null

  constructor(initialAst: ContentAST, initialSelection: AstSelection, options: UndoRedoManagerOptions = {}) {
    this.ast = cloneAST(initialAst)
    this.selection = { ...initialSelection }
    this.options = {
      maxHistorySize: options.maxHistorySize || 100,
      coalescingConfig: options.coalescingConfig || {},
      enableNormalization: options.enableNormalization !== undefined ? options.enableNormalization : true,
    }
    this.coalescingConfig = {
      ...defaultCoalescingConfig,
      ...options.coalescingConfig,
    }
  }

  /**
   * Gets current AST
   */
  public getCurrentAST(): ContentAST {
    return this.ast
  }

  /**
   * Gets current selection
   */
  public getCurrentSelection(): AstSelection {
    return this.selection
  }

  /**
   * Sets current selection without capturing state
   */
  public setCurrentSelection(selection: AstSelection): void {
    this.selection = { ...selection }
  }

  /**
   * Captures a state change as an undo operation (similar to original editor's captureTypingOperation)
   * This creates a simple state-based undo operation
   */
  /**
   * Temporarily disable state capture during programmatic changes
   */
  public setUpdatingSelection(updating: boolean): void {
    this.isUpdatingSelection = updating
  }

  /**
   * Store Lexical EditorStates for the current batch.
   * beforeState: the state before this change (first call per batch is kept as editorStateBefore).
   * afterState: the state after this change (always updated, becomes editorStateAfter on flush).
   * Skipped during undo/redo to avoid capturing stale states.
   */
  public setEditorState(beforeState: unknown, afterState: unknown): void {
    if (this.isUpdatingSelection || this.isApplyingTransaction) {
      return
    }
    if (!this.pendingEditorState) {
      this.pendingEditorState = beforeState
    }
    this.currentEditorState = afterState
  }

  // Debounce timer for capturing undo state
  private captureDebounceTimer: ReturnType<typeof setTimeout> | null = null
  private pendingAST: ContentAST | null = null
  private pendingSelection: AstSelection | null = null

  public captureStateChange(newAST: ContentAST, newSelection: AstSelection, _reason: string = 'typing'): void {
    // Don't capture if we're currently applying a transaction (during undo/redo)
    if (this.isApplyingTransaction) {
      return
    }

    // Skip capture if we're updating selection programmatically
    if (this.isUpdatingSelection) {
      logger.debug('💾 Skipping state capture during selection update')
      return
    }

    // If the current batch is on a different block, flush it before starting a new one.
    // This prevents a spurious initial onChange from poisoning the batch's selectionBefore.
    if (this.pendingAST && this.pendingSelection && this.pendingSelection.blockId !== newSelection.blockId) {
      logger.debug(
        '[UNDO] block changed (%s → %s), flushing old batch',
        this.pendingSelection.blockId,
        newSelection.blockId,
      )
      this.flushPendingCapture()
    }

    // PERFORMANCE FIX: Always update the current AST reference immediately
    // but debounce the expensive clone operations for undo history
    const previousAST = this.ast
    this.ast = newAST
    this.selection = newSelection

    // Store pending state for debounced capture
    if (!this.pendingAST) {
      // First change in this batch - remember the original state
      this.pendingAST = previousAST
      this.pendingSelection = { ...newSelection }
      // pendingSelection captured for this batch
    } else {
      // batch continues
    }

    // Clear existing timer
    if (this.captureDebounceTimer) {
      clearTimeout(this.captureDebounceTimer)
    }

    // Debounce: only create undo entry after 300ms of no typing
    this.captureDebounceTimer = setTimeout(() => {
      this.flushPendingCapture()
    }, 300)
  }

  /**
   * Flush pending capture - creates the actual undo entry
   * Called after debounce timeout or before undo/redo
   */
  private flushPendingCapture(): void {
    if (!this.pendingAST) {
      return
    }

    // Clear timer if it's still running
    if (this.captureDebounceTimer) {
      clearTimeout(this.captureDebounceTimer)
      this.captureDebounceTimer = null
    }

    const previousAST = this.pendingAST
    const previousSelection = this.pendingSelection || this.selection
    const savedEditorStateBefore = this.pendingEditorState
    const savedEditorStateAfter = this.currentEditorState
    this.pendingAST = null
    this.pendingSelection = null
    this.pendingEditorState = null
    this.currentEditorState = null
    logger.debug('[UNDO] flush: hasEditorState=%s', !!savedEditorStateBefore)

    // Now do the expensive clone operations (only once per typing session).
    // The forward and inverse operations describe the same two documents in
    // opposite directions, so they share one clone of each rather than taking
    // four: an undo step costs two AST copies, not four. Safe because stored
    // ASTs are already treated as immutable — applyOperations' state_change
    // fast path hands one straight back as the live AST, so anything mutating
    // one in place would have corrupted the history long before this.
    const beforeSnapshot = cloneAST(previousAST)
    const afterSnapshot = cloneAST(this.ast)

    const operation: AstOperation = {
      type: 'state_change',
      blockId: this.ast.blocks[0]?.id || 'def01',
      data: {
        previousAST: beforeSnapshot,
        newAST: afterSnapshot,
        previousSelection: { ...previousSelection },
        newSelection: { ...this.selection },
      },
      timestamp: Date.now(),
    }

    // Create inverse operation
    const inverseOperation: AstOperation = {
      type: 'state_change',
      blockId: previousAST.blocks[0]?.id || 'def01',
      data: {
        previousAST: afterSnapshot,
        newAST: beforeSnapshot,
        previousSelection: { ...this.selection },
        newSelection: { ...previousSelection },
      },
      timestamp: Date.now(),
    }

    // Create transaction
    const transaction: AstTransaction = {
      id: uuidv4(),
      operations: [operation],
      inverseOperations: [inverseOperation],
      selectionBefore: { ...previousSelection },
      selectionAfter: { ...this.selection },
      reason: 'typing',
      timestamp: Date.now(),
      coalesceKey: 'typing',
      editorStateBefore: savedEditorStateBefore,
      editorStateAfter: savedEditorStateAfter,
    }

    // Add transaction to undo stack
    this.addTransaction(transaction)

    // Clear redo stack on new changes
    this.redoStack = []
  }

  /**
   * Gets undo/redo state
   */
  public getState(): UndoRedoState {
    return {
      canUndo: this.undoStack.length > 0,
      canRedo: this.redoStack.length > 0,
      undoDescription: this.undoStack[this.undoStack.length - 1]?.reason,
      redoDescription: this.redoStack[this.redoStack.length - 1]?.reason,
      historySize: this.undoStack.length,
    }
  }

  /**
   * Executes a transaction (group of operations)
   */
  public executeTransaction(
    operations: AstOperation[],
    newSelection: AstSelection,
    reason: string,
    coalesceKey?: string,
  ): ApplyResult {
    logger.debug(
      '🔄 executeTransaction called with operations:',
      operations.map((op) => ({ type: op.type, blockId: op.blockId })),
    )
    logger.debug(
      '🔄 executeTransaction: Current AST before:',
      JSON.stringify(
        this.ast.blocks.map((b) => ({ id: b.id, type: b.type })),
        null,
        2,
      ),
    )

    if (this.isApplyingTransaction) {
      return { success: false, error: 'Cannot execute transaction while applying another' }
    }

    // Apply operations to get new AST
    const result = applyOperations(this.ast, operations)
    logger.debug(
      '🔄 executeTransaction: applyOperations result:',
      result.success ? 'SUCCESS' : 'FAILED',
      result.error || '',
    )
    if (!result.success || !result.ast) {
      return result
    }
    logger.debug(
      '🔄 executeTransaction: New AST after applyOperations:',
      JSON.stringify(
        result.ast.blocks.map((b) => ({ id: b.id, type: b.type })),
        null,
        2,
      ),
    )

    // Create inverse operations for undo
    logger.debug('🔄 executeTransaction: Creating inverse operations...')
    const inverseOperations: AstOperation[] = []
    for (let i = operations.length - 1; i >= 0; i--) {
      try {
        const inverse = createInverseOperation(operations[i])
        inverseOperations.push(inverse)
      } catch (error) {
        logger.error('🔄 executeTransaction: Failed to create inverse operation:', error)
        return { success: false, error: `Failed to create inverse operation: ${error}` }
      }
    }
    logger.debug('🔄 executeTransaction: Inverse operations created successfully')

    // Create transaction
    logger.debug('🔄 executeTransaction: Creating transaction...')
    const transaction: AstTransaction = {
      id: uuidv4(),
      operations,
      inverseOperations,
      selectionBefore: { ...this.selection },
      selectionAfter: { ...newSelection },
      reason,
      timestamp: Date.now(),
      coalesceKey,
    }

    // Try to coalesce with previous transaction
    logger.debug('🔄 executeTransaction: Checking coalescing...')
    if (this.shouldCoalesceTransaction(transaction)) {
      logger.debug('🔄 executeTransaction: Should coalesce - attempting coalescing')
      const lastTransaction = this.undoStack[this.undoStack.length - 1]
      const coalescedTransaction = this.coalesceTransactions(lastTransaction, transaction)

      if (coalescedTransaction) {
        logger.debug('🔄 executeTransaction: Coalescing successful - replacing last transaction')
        // Replace last transaction with coalesced one
        this.undoStack[this.undoStack.length - 1] = coalescedTransaction
      } else {
        logger.debug('🔄 executeTransaction: Coalescing failed - adding new transaction')
        // Add new transaction
        this.addTransaction(transaction)
      }
    } else {
      logger.debug('🔄 executeTransaction: No coalescing - adding new transaction')
      // Add new transaction
      this.addTransaction(transaction)
    }

    // Update state
    this.ast = this.options.enableNormalization ? normalizeAST(result.ast) : result.ast
    this.selection = { ...newSelection }
    logger.debug(
      '🔄 executeTransaction: Final AST after update:',
      JSON.stringify(
        this.ast.blocks.map((b) => ({ id: b.id, type: b.type })),
        null,
        2,
      ),
    )

    // Clear redo stack when new operation is performed
    this.redoStack = []

    return { success: true, ast: this.ast }
  }

  /**
   * Executes a single operation
   */
  public executeOperation(operation: AstOperation, newSelection: AstSelection, reason?: string): ApplyResult {
    return this.executeTransaction([operation], newSelection, reason || operation.type)
  }

  /**
   * Undoes the last transaction.
   *
   * Orientation pass: if the cursor isn't in the same block as the next undo
   * target, the first press only moves the cursor there and returns
   * `navigationOnly: true` without popping the stack. The next press performs
   * the actual undo. Matches Google Docs–style "see where it'll happen first".
   */
  /**
   * Is the cursor already sitting on what this transaction changed?
   *
   * The orientation pass compares block ids, which assumes ids survive an edit.
   * Transforms that replace a block don't preserve them — toggling a paragraph
   * into a list item mints a new id while the caret never visibly moves — so the
   * ids differ, the orientation pass fires, and the first Cmd+Z appears to do
   * nothing. When the cursor's block is one this transaction created (undo) or
   * destroys (redo), the user is already looking at the change: no orientation.
   */
  private cursorSitsOnTransaction(transaction: AstTransaction, direction: 'undo' | 'redo'): boolean {
    const data = (transaction.operations[0] as { data?: { previousAST?: ContentAST; newAST?: ContentAST } } | undefined)
      ?.data
    const astWithoutTheChange = direction === 'undo' ? data?.previousAST : data?.newAST
    if (!astWithoutTheChange) {
      return false
    }
    return !findAnyById(astWithoutTheChange, this.selection.blockId)
  }

  public undo(): ApplyResult {
    // Flush any pending capture before undoing
    this.flushPendingCapture()

    if (this.undoStack.length === 0) {
      return { success: false, error: 'Nothing to undo' }
    }

    const transaction = this.undoStack[this.undoStack.length - 1]
    const target = transaction.selectionBefore

    if (target.blockId !== this.selection.blockId && !this.cursorSitsOnTransaction(transaction, 'undo')) {
      this.selection = { ...target }
      return {
        success: true,
        ast: this.ast,
        selection: { ...target },
        navigationOnly: true,
      }
    }

    // Pop and apply
    this.undoStack.pop()
    this.isApplyingTransaction = true

    try {
      // Apply inverse operations
      const result = applyOperations(this.ast, transaction.inverseOperations)
      if (!result.success || !result.ast) {
        // Restore transaction to stack on failure
        this.undoStack.push(transaction)
        return result
      }

      // Update state
      this.ast = this.options.enableNormalization ? normalizeAST(result.ast) : result.ast
      this.selection = { ...transaction.selectionBefore }

      // Move transaction to redo stack
      this.redoStack.push(transaction)

      return {
        success: true,
        ast: this.ast,
        selection: { ...this.selection },
        editorState: transaction.editorStateBefore,
      }
    } finally {
      this.isApplyingTransaction = false
    }
  }

  /**
   * Redoes the last undone transaction.
   *
   * Same orientation pass as undo: if the cursor isn't in the same block as
   * the next redo target, the first press only moves the cursor and returns
   * `navigationOnly: true`. The next press actually redoes.
   */
  public redo(): ApplyResult {
    // Flush any pending capture before redoing
    this.flushPendingCapture()

    if (this.redoStack.length === 0) {
      return { success: false, error: 'Nothing to redo' }
    }

    const transaction = this.redoStack[this.redoStack.length - 1]
    const target = transaction.selectionAfter

    // A redo may recreate the target block (for example a paragraph after an embed).
    // There is nowhere to orient the caret until that transaction is applied.
    if (
      target.blockId !== this.selection.blockId &&
      findAnyById(this.ast, target.blockId) &&
      !this.cursorSitsOnTransaction(transaction, 'redo')
    ) {
      this.selection = { ...target }
      return {
        success: true,
        ast: this.ast,
        selection: { ...target },
        navigationOnly: true,
      }
    }

    // Pop and apply
    this.redoStack.pop()
    this.isApplyingTransaction = true

    try {
      // Apply original operations
      const result = applyOperations(this.ast, transaction.operations)
      if (!result.success || !result.ast) {
        // Restore transaction to stack on failure
        this.redoStack.push(transaction)
        return result
      }

      // Update state
      this.ast = this.options.enableNormalization ? normalizeAST(result.ast) : result.ast
      this.selection = { ...transaction.selectionAfter }

      // Move transaction back to undo stack
      this.undoStack.push(transaction)

      return {
        success: true,
        ast: this.ast,
        selection: { ...this.selection },
        editorState: transaction.editorStateAfter,
      }
    } finally {
      this.isApplyingTransaction = false
    }
  }

  /**
   * Clears all undo/redo history
   */
  public clearHistory(): void {
    this.undoStack = []
    this.redoStack = []
  }

  /**
   * Sets a new AST state (clears history)
   */
  public setAST(ast: ContentAST, selection: AstSelection): void {
    this.ast = cloneAST(ast)
    this.selection = { ...selection }
    this.clearHistory()
  }

  /**
   * Updates current selection without affecting undo/redo
   */
  public updateSelection(selection: AstSelection): void {
    this.selection = { ...selection }
  }

  /**
   * Creates a checkpoint (useful before major operations)
   */
  public createCheckpoint(_reason: string): void {
    // This could be implemented as a special transaction type
    // For now, it's a placeholder for future enhancement
  }

  /**
   * Gets transaction history (for debugging)
   */
  public getHistory(): { undo: AstTransaction[]; redo: AstTransaction[] } {
    return {
      undo: [...this.undoStack],
      redo: [...this.redoStack],
    }
  }

  /**
   * Adds a transaction to the undo stack
   */
  private addTransaction(transaction: AstTransaction): void {
    this.undoStack.push(transaction)
    // logger.debug('💾 Added transaction to undo stack. New size:', this.undoStack.length)
    // logger.debug('💾 Undo stack operations:', this.undoStack.map((tx, i) => `${i}: ${tx.operations[0]?.type} (${tx.reason}) at ${new Date(tx.timestamp).toLocaleTimeString()}`))

    // Limit history size
    if (this.undoStack.length > this.options.maxHistorySize) {
      this.undoStack.shift()
    }
  }

  /**
   * Checks if a transaction should be coalesced with the previous one
   */
  private shouldCoalesceTransaction(transaction: AstTransaction): boolean {
    if (!this.coalescingConfig.enabled || this.undoStack.length === 0) {
      return false
    }

    const lastTransaction = this.undoStack[this.undoStack.length - 1]

    // Check coalesce key
    if (transaction.coalesceKey && transaction.coalesceKey === lastTransaction.coalesceKey) {
      return true
    }

    // Check if operations can be coalesced
    if (transaction.operations.length === 1 && lastTransaction.operations.length === 1) {
      return canCoalesceOperations(lastTransaction.operations[0], transaction.operations[0], this.coalescingConfig)
    }

    return false
  }

  /**
   * Coalesces two transactions into one
   */
  private coalesceTransactions(transaction1: AstTransaction, transaction2: AstTransaction): AstTransaction | null {
    try {
      // For now, only coalesce single-operation transactions
      if (transaction1.operations.length === 1 && transaction2.operations.length === 1) {
        const coalescedOperation = coalesceOperations(transaction1.operations[0], transaction2.operations[0])

        const coalescedInverse = createInverseOperation(coalescedOperation)

        return {
          id: transaction1.id, // Keep original ID
          operations: [coalescedOperation],
          inverseOperations: [coalescedInverse],
          selectionBefore: transaction1.selectionBefore,
          selectionAfter: transaction2.selectionAfter,
          reason: transaction1.reason, // Keep original reason
          timestamp: transaction1.timestamp, // Keep original timestamp
          coalesceKey: transaction1.coalesceKey,
        }
      }

      return null
    } catch {
      return null
    }
  }
}

/**
 * Convenience function to create a new undo/redo manager
 */
export const createUndoRedoManager = (
  ast: ContentAST,
  selection: AstSelection,
  options?: UndoRedoManagerOptions,
): AstUndoRedoManager => {
  return new AstUndoRedoManager(ast, selection, options)
}

/**
 * Selection helper functions
 */
export const createSelection = (blockId: string, offset: number, length?: number): AstSelection => {
  return {
    blockId,
    offset,
    length,
  }
}

export const isValidAstSelection = (selection: AstSelection): boolean => {
  return Boolean(
    selection.blockId &&
      typeof selection.offset === 'number' &&
      selection.offset >= 0 &&
      (selection.length === undefined || (typeof selection.length === 'number' && selection.length >= 0)),
  )
}
